import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Server, Shield, Zap, Globe, Clock, Lock, Image } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteHosting = () => {
  const faqs = [
    {
      question: "What is landscaping website hosting?",
      answer: "Website hosting provides the server space where your landscaping website files live. It keeps your site online 24/7 so potential clients can view your portfolio and contact you anytime."
    },
    {
      question: "How much does landscaping website hosting cost?",
      answer: "Basic hosting costs $5–$20/month. Quality managed hosting for portfolio-heavy sites runs $30–$75/month. Our subscription includes hosting in the $200–$300/month price."
    },
    {
      question: "Why do landscaping websites need special hosting considerations?",
      answer: "Landscaping sites have large image files (portfolio photos). Standard cheap hosting often can't handle image-heavy sites efficiently, resulting in slow load times and lost leads."
    },
    {
      question: "Is hosting included in your subscription?",
      answer: "Yes. Managed hosting, SSL, CDN, and image optimization are all included in the $200–$300/month subscription. No separate hosting fees."
    },
    {
      question: "Does hosting affect how fast my portfolio images load?",
      answer: "Absolutely. Quality hosting with CDN and image optimization makes portfolio galleries load fast. Cheap hosting often struggles with high-resolution project photos."
    },
    {
      question: "What's the difference between shared and managed hosting?",
      answer: "Shared hosting crams many sites on one server (cheaper, slower). Managed hosting provides dedicated resources, automatic updates, and support—important for business sites."
    },
    {
      question: "Does hosting affect my Google rankings?",
      answer: "Yes. Site speed is a ranking factor. Fast hosting = faster site = better rankings. This matters especially for landscaping sites with many images."
    },
    {
      question: "Do I need an SSL certificate?",
      answer: "Yes. SSL (the padlock icon) is required for security and Google rankings. Quality hosting includes free SSL. Our subscription includes SSL automatically."
    },
    {
      question: "Can I switch hosting later if I'm not happy?",
      answer: "Yes, but it's technical and risky. With our subscription, we handle all hosting—you never need to manage or switch providers."
    },
    {
      question: "What happens if my hosting goes down?",
      answer: "With cheap hosting, you're on your own. With managed hosting or our subscription, uptime is monitored and issues are resolved quickly—often before you notice."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-hosting/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website hosting for landscaping companies optimized for portfolio-heavy sites."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-hosting/#webpage",
    "name": "Landscaping Website Hosting | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-hosting/",
    "description": "Fast landscaping website hosting optimized for portfolio images. SSL, CDN, and uptime monitoring included in subscription.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-hosting/#service",
    "name": "Landscaping Website Hosting",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Managed website hosting for landscaping companies optimized for image-heavy portfolio sites.",
    "areaServed": "United States",
    "serviceType": "Website Hosting"
  };

  return (
    <>
      <SEO 
        title="Landscaping Website Hosting | Leaf Rush"
        description="Fast landscaping website hosting optimized for portfolio images. SSL, CDN, and uptime monitoring included in subscription."
        keywords="landscaping website hosting, landscaper site hosting, web hosting landscaping, landscaping server hosting"
        canonical="https://www.leafrushmarketing.com/landscaping-website-hosting/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-hosting/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Portfolio-Optimized Hosting
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Hosting Built for Image-Heavy Sites
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Fast loading for portfolio galleries, reliable uptime, SSL security—all optimized for landscaping websites with lots of project photos.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Landscaping website hosting keeps your portfolio-heavy site fast and online. Quality hosting for image-heavy sites costs $30–$75/month standalone. Our subscription includes optimized hosting in $200–$300/month.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're looking for a complete website solution, visit our main{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What's Included in Our Hosting
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Image className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Image Optimization</p>
                    <p className="text-sm text-muted-foreground">
                      High-res portfolio photos optimized for fast loading without quality loss.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Fast Load Speeds</p>
                    <p className="text-sm text-muted-foreground">
                      Optimized servers and CDN for fast loading—critical for image-heavy sites.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Server className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Reliable Uptime</p>
                    <p className="text-sm text-muted-foreground">
                      99.9% uptime monitoring so your site is always available for potential clients.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">SSL Security</p>
                    <p className="text-sm text-muted-foreground">
                      HTTPS encryption included—required for security and Google rankings.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Portfolio Sites Need Better Hosting */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Landscaping Portfolios Need Premium Hosting
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Large Image Files</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Before/after project photos are large. Cheap hosting can't handle them efficiently.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Gallery Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Visitors browse multiple projects. Fast hosting keeps galleries responsive.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Mobile Experience</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Most leads come from phones. Images must load fast on mobile connections.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground">
                  See{" "}
                  <NavLink to="/landscaping-portfolio-website/" className="text-primary underline hover:no-underline">
                    landscaping portfolio website guide
                  </NavLink>{" "}
                  for portfolio optimization tips.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Hosting Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Hosting Options Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Monthly Cost</th>
                      <th className="text-left py-3 px-4">Portfolio Speed</th>
                      <th className="text-left py-3 px-4">Support</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">Included in $200–$300</td>
                      <td className="py-3 px-4">Fast (optimized)</td>
                      <td className="py-3 px-4">Full support</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Managed Hosting</td>
                      <td className="py-3 px-4">$30–$75</td>
                      <td className="py-3 px-4">Fast</td>
                      <td className="py-3 px-4">Technical support</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Shared Hosting</td>
                      <td className="py-3 px-4">$5–$20</td>
                      <td className="py-3 px-4">Often slow</td>
                      <td className="py-3 px-4">Limited</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/landscaping-website-cost/" className="text-primary underline hover:no-underline">
                  landscaping website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Landscaping Website Hosting
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/landscaping-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional landscaping websites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-examples/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Examples</h3>
                  <p className="text-sm text-muted-foreground">See examples of effective landscaping sites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for landscaping sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for Portfolio-Optimized Hosting?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes fast hosting built for landscaping portfolios.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteHosting;
